import React, { useEffect, useState, useRef } from "react"
import * as dateHelper from "../../booking/helpers/date"
import * as endpoints from "../../api/endpoints"
import da from "date-fns/locale/da"
import enGB from "date-fns/locale/en-GB"
import Svg from "../_svgs/svg"
import SearchOverlay from "../flight-search-hero/search-overlay"
import OutsideClickTrigger from "../../booking/components/common/OutsideClickTrigger"
import * as SelectionFactoryHelper from "../helpers/SelectionFactoryHelper"
import { DateTime } from "luxon"

export default function FlightSearchBar({
  ariaLiveUpdateMessage,
  allFlightsFrom,
  allFlightsLimit,
  allFlightsTo,
  allFlightsWithCarrier,
  arrivalsPage,
  arrivalsTabIcon,
  arrivalsTabLabel,
  carriersLimit,
  departuresPage,
  departuresTabIcon,
  departuresTabLabel,
  icon,
  iconUpload,
  locale,
  mainText,
  minCharsBeforeSearch,
  noResultsLabel,
  numberOfFlights,
  numberOfFlightsMobile,
  searchBoxPlaceholder,
  searchBoxPlaceholderMobile,
  searchHelperText,
  seeMoreFlightsArrivals,
  seeMoreFlightsDepartures,
  showTime,
  timeLabel,
  clearSearchAriaLabel,
}) {
  const dtLocale = dateHelper.luxonLocale(locale)
  const zone = "Europe/Copenhagen"
  const cphTimeConvert = DateTime.local().setLocale(dtLocale).setZone(zone) // gets local time and changes timezone to copenhagen to get correct time
  const cphTime = cphTimeConvert.setZone("local", { keepLocalTime: true }) // locks in copenhagen time and switches back to local timezone. This ensures the time will stay CPH time when we later converts the DateTime to a JS Date object.

  const [q, setQ] = useState("") //string that queries flights.
  const [searchDate, setSearchDate] = useState(cphTime.set({ milliseconds: 0, seconds: 0, minutes: 60 }).minus({ hours: 1 }))
  const [startDate, setStartDate] = useState(cphTime.set({ milliseconds: 0, seconds: 0, minutes: 60 }).minus({ hours: 1 }))
  const [minDate, setMinDate] = useState(startDate.minus({ days: 1 }))

  const [flights, setFlights] = useState([])
  const [direction, setDirection] = useState("D")
  const [showOverlay, setShowOverlay] = useState(false)
  const [datepickerOpen, setDatepickerOpen] = useState(false)
  const [airports, setAirports] = useState([])
  const [airlines, setAirlines] = useState([])
  const [mobileInputFocus, setMobileInputFocus] = useState(false)
  const [overInput, setOverInput] = useState(false)

  // maxTime and minTime currently does not have use for a setter.
  // reconsider if this should be in state or not.
  const [minTime] = useState(cphTime.set({ hours: 0, minutes: 0, seconds: 0, milliseconds: 0 }))
  const [maxTime] = useState(cphTime.set({ hours: 23, minutes: 0, seconds: 0, milliseconds: 0 }))

  const [inputFocused, setInputFocused] = useState(false)

  const searchInputRef = useRef(null)
  const resultsRef = useRef(null)
  const resultsUpdateTimeout = useRef(null)
  const resetInputButtonRef = useRef(null)

  const initialState = {
    flights,
    direction,
    q,
    showOverlay,
    searchDate,
    minDate,
    datepickerOpen,
    minTime,
    maxTime,
    airports,
    airlines,
    mobileInputFocus,
    startDate,
    overInput,
    search,
  }

  const [screenWidth, setScreenWidth] = useState(window.innerWidth)

  useEffect(() => {
    const updateScreenWidth = () => {
      setScreenWidth(window.innerWidth)
    }

    window.addEventListener("resize", updateScreenWidth)

    return () => {
      window.removeEventListener("resize", updateScreenWidth)
    }
  }, [])

  useEffect(() => {
    const handleFlightSearchRedirect = (event) => {
      let flightId = searchInputRef.current
      let flightIsFocused = document.activeElement === flightId
      if (event.key === "Enter" && q && flightIsFocused) {
        window.location.href = `${direction === "D" ? departuresPage : arrivalsPage}?q=${q}&date=${searchDate.toFormat(
          "dd / MM / yyyy",
        )}&time=${searchDate.toFormat("HH")}`
      }
    }
    document.addEventListener("keydown", handleFlightSearchRedirect)
    return () => {
      document.removeEventListener("keydown", handleFlightSearchRedirect)
    }
  }, [q, searchDate, direction])

  useEffect(() => {
    const handleExitFlightSearchForm = (event) => {
      if (event.key === "Escape") {
        setShowOverlay(false)
        const input = searchInputRef.current
        if (input) {
          input.blur()
        }
      }
    }
    document.addEventListener("keydown", handleExitFlightSearchForm)
    return () => {
      document.removeEventListener("keydown", handleExitFlightSearchForm)
    }
  }, [])

  const handleDirectionChange = (direction, wasFocused) => {
    if (screenWidth < 768) {
      const input = searchInputRef.current
      if (input && wasFocused) {
        input.focus()
        setTimeout(() => {
          setMobileInputFocus(true)
        }, 20)
      }
    }
    setDirection(direction)
  }

  //Calling search whenever Query, searchDate and Direction changes.
  //Validation for if a search should occur happens in the search() method.
  useEffect(() => {
    search()
  }, [q, searchDate, direction])

  useEffect(() => {
    const handleFlightSearchRedirect = (event) => {
      let flightId = searchInputRef.current
      let flightIsFocused = document.activeElement === flightId

      if (event.key === "Enter" && q && flightIsFocused) {
        window.location.href = `${direction === "D" ? departuresPage : arrivalsPage}?q=${q}&date=${searchDate.toFormat(
          "dd / MM / yyyy",
        )}&time=${searchDate.toFormat("HH")}`
      }
    }

    document.addEventListener("keydown", handleFlightSearchRedirect)

    return () => {
      document.removeEventListener("keydown", handleFlightSearchRedirect)
    }
  }, [q, searchDate, direction])

  useEffect(() => {
    const handleExitFlightSearchForm = (event) => {
      if (event.key === "Escape") {
        setShowOverlay(false)
        const input = document.querySelector("#flight-search-input")
        if (input) {
          input.blur()
        }
      }
    }

    document.addEventListener("keydown", handleExitFlightSearchForm)

    return () => {
      document.removeEventListener("keydown", handleExitFlightSearchForm)
    }
  }, [])

  const updateResultsMessage = () => {
    if (q.length < minCharsBeforeSearch) return
    const shortDateOptions = {
      month: "short",
      day: "2-digit",
    }
    const shortSearchDate = new Date(searchDate).toLocaleDateString(locale.code, shortDateOptions)

    if (flights && flights.length === 0) {
      const formattedNoResultsLabel = noResultsLabel.formatUnicorn({ input: q, date: shortSearchDate })
      resultsUpdateTimeout.current = setTimeout(() => {
        if (resultsRef.current) {
          resultsRef.current.innerHTML = `<p lang="${locale}">${formattedNoResultsLabel}</p>`
        }
      }, 2000)
      return
    }

    if (ariaLiveUpdateMessage == null) return

    if (resultsUpdateTimeout.current) {
      clearTimeout(resultsUpdateTimeout.current)
    }

    resultsUpdateTimeout.current = setTimeout(() => {
      if (resultsRef.current) {
        resultsRef.current.innerHTML = `<p lang="${locale}">${ariaLiveUpdateMessage}</p>`
      }
    }, 2000)
  }

  useEffect(() => {
    updateResultsMessage()
    return () => {
      if (resultsUpdateTimeout.current) {
        clearTimeout(resultsUpdateTimeout.current)
      }
    }
  }, [flights])

  const search = async () => {
    if (q.length >= (minCharsBeforeSearch ?? 2)) {
      const dt = searchDate
      const zone = "Europe/Copenhagen"
      const cphTime = dt.setZone(zone, { keepLocalTime: true }) // set zone to copenhagen and keep local time, which is already cph time
      const start = cphTime.toUTC() // convert to UTC which is what Solr expects
      const end = cphTime.endOf("day").toUTC() // convert to UTC which is what Solr expects
      const flightsData = { direction: direction, query: q, startDateTime: start, endDateTime: end, language: locale }

      // use this data and call GetAirports endpoint to get airports from solr core instead of flightinfo
      // const airportsData = { language: locale, query: q, limit: allFlightsLimit }
      const returns = await Promise.all([getFlights(flightsData), getAirports(flightsData)])
        .then((responses) => {
          return Promise.all(
            responses.map((response) => {
              return response
            }),
          )
        })
        .then((data) => {
          return data
        })
        .catch((err) => {
          console.log(err)
        })
      const flights = returns[0]
      const airports = returns[1]
      const airlines = getAirlines(flights)
      setFlights(flights)
      setAirports(airports)
      setAirlines(airlines)
    }
  }

  const handleDateChange = async (dateInput) => {
    let dateTime = DateTime.fromJSDate(dateInput)
    const startOfMinDate = minDate.startOf("day")
    const startOfNewDate = dateTime.startOf("day")
    const time = dateTime.toFormat("HH:mm")

    setSearchDate((prevState) => {
      const startOfPrevDate = prevState.startOf("day")
      const prevTime = prevState.toFormat("HH:mm")

      if (startOfMinDate.toMillis() === startOfNewDate.toMillis()) {
        if (dateTime <= minDate || time === prevTime) {
          setMinDate(dateTime)
          return dateTime
        }
        return dateTime
      } else {
        if (startOfPrevDate.toMillis() !== startOfNewDate.toMillis()) {
          dateTime = dateTime.set({ hours: 0, minutes: 0, seconds: 0, milliseconds: 0 })
        }
        return dateTime
      }
    })
  }

  const getAirlines = (flights) => {
    let airlines = []
    if (flights && flights.length > 0) {
      const result = Array.from(new Set(flights.map((f) => f.IataAirline))).map((iata) => {
        return {
          iata: iata,
          name: flights.find((f) => f.IataAirline === iata).Airline,
          nameMobile: flights.find((f) => f.IataAirline === iata).AirlineMobile,
        }
      })
      airlines = result
    }
    return airlines
  }

  const getAirports = async (data) => {
    try {
      const res = await endpoints.getAirportsFromFlights(data)
      const data_2 = res.data
      let airports = []
      if (data_2) {
        airports = data_2
      }
      return airports
    } catch (err) {
      console.log(err)
    }
  }

  const getFlights = async (data) => {
    try {
      const res = await endpoints.getFlightInfoTable(data)
      const data_2 = res.data
      let flights = []
      if (data_2) {
        flights = data_2
      }
      return flights
    } catch (err) {
      console.log(err)
    }
  }

  const handleInputFocus = () => {
    if (q.length > 0) {
      setShowOverlay(true)
    }
  }

  const handleInput = (event, inputSearch = true) => {
    setQ(event.target.value)
    if (event.target.value.length >= 1) {
      setShowOverlay(true)
    } else {
      setShowOverlay(false)
    }
  }

  const handleCloseOverlay = () => {
    searchInputRef.current.focus()
    setFlights([])
    setAirports([])
    setShowOverlay(false)
    setDatepickerOpen(false)
    setQ("")
    setStartDate(cphTime.set({ milliseconds: 0, seconds: 0, minutes: 60 }).minus({ hours: 1 }))
    resultsRef.current.textContent = ""
  }

  const handleDatepicker = () => {
    setDatepickerOpen(!datepickerOpen)
  }

  const closeDatepicker = () => {
    setDatepickerOpen(false)
  }

  const handleOutsideClick = () => {
    if (!overInput) {
      setShowOverlay(false)
      setDatepickerOpen(false)
    }
  }
  const containerRef = useRef(null)

  useEffect(() => {
    const handleFocusIn = (event) => {
      const relatedTarget = event.target
      // exceptions to focus rule
      if (
        containerRef.current?.contains(relatedTarget) ||
        searchInputRef.current === relatedTarget ||
        resetInputButtonRef.current === relatedTarget
      ) {
        return
      }
      setShowOverlay(false)
      setDatepickerOpen(false)
    }
    document.addEventListener("focusin", handleFocusIn)

    return () => {
      document.removeEventListener("focusin", handleFocusIn)
    }
  }, [])

  const selectionFactoryIcon = SelectionFactoryHelper.getFromSelectionFactory(icon)

  return (
    <section className={`deck--front`}>
      <header className="section-header section-header-search">
        <div className="section-header__inner">
          <div className="w--normal">
            <div className="flight-searchbox">
              <div className="form">
                <div className="form__section form__section--light form__section--fat">
                  <div ref={containerRef} className={`form__section__inner ${screenWidth < 768 && showOverlay ? "is-active" : ""}`}>
                    <OutsideClickTrigger onOutsideClick={() => handleOutsideClick()}>
                      <div className="form__field form__field--text form__field--text-search form__field--text--no-label is-bound is-filled">
                        <span className="form__valid"></span>
                        <div className="headline-container">
                          {iconUpload ? (
                            <img aria-hidden="true" tabIndex={-1} src={iconUpload} alt={""} />
                          ) : selectionFactoryIcon ? (
                            <Svg label={""} isDecorative={true} iconName={selectionFactoryIcon} />
                          ) : (
                            ""
                          )}
                          <h2>{mainText}</h2>
                        </div>
                        <input
                          ref={searchInputRef}
                          className="form__item"
                          id="flight-search-input"
                          autoComplete="off"
                          autoCapitalize="none"
                          name="q"
                          type="text"
                          placeholder={screenWidth < 768 ? searchBoxPlaceholderMobile : searchBoxPlaceholder}
                          value={q}
                          onChange={(e) => handleInput(e, true)}
                          onMouseEnter={() => setOverInput(true)}
                          onMouseLeave={() => setOverInput(false)}
                          onFocus={() => {
                            handleInputFocus(), setInputFocused(true)
                          }}
                          onBlur={() => {
                            setInputFocused(false)
                          }}
                          aria-controls="results"
                        />
                        <div ref={resultsRef} id="results" aria-live="polite" className="visually-hidden" aria-hidden={!inputFocused}></div>

                        {screenWidth >= 768 ? (
                          <button
                            aria-label={clearSearchAriaLabel}
                            tabIndex={showOverlay ? 0 : -1}
                            className={`search-button ${showOverlay ? "active" : ""}`}
                            onClick={showOverlay ? handleCloseOverlay : undefined}
                          >
                            <div className={`search-icon`}></div>
                          </button>
                        ) : (
                          ""
                        )}
                        {screenWidth < 768 && showOverlay ? (
                          <button
                            tabIndex={showOverlay ? 0 : -1}
                            aria-label={clearSearchAriaLabel}
                            className={`search-button ${showOverlay ? "active" : ""}`}
                            onClick={showOverlay ? handleCloseOverlay : undefined}
                          >
                            {/* <Svg iconName={"close"} fillColor={"#071143"} /> awaiting feedback from CPH on what to do with this. */}
                          </button>
                        ) : (
                          ""
                        )}
                      </div>
                      <SearchOverlay
                        {...initialState}
                        allFlightsFrom={allFlightsFrom}
                        allFlightsLimit={allFlightsLimit}
                        allFlightsTo={allFlightsTo}
                        allFlightsWithCarrier={allFlightsWithCarrier}
                        arrivalsPage={arrivalsPage}
                        arrivalsTabIcon={arrivalsTabIcon}
                        arrivalsTabLabel={arrivalsTabLabel}
                        carriersLimit={carriersLimit}
                        departuresPage={departuresPage}
                        departuresTabIcon={departuresTabIcon}
                        departuresTabLabel={departuresTabLabel}
                        minCharsBeforeSearch={minCharsBeforeSearch}
                        noResultsLabel={noResultsLabel}
                        numberOfFlights={numberOfFlights}
                        numberOfFlightsMobile={numberOfFlightsMobile}
                        searchHelperText={searchHelperText}
                        seeMoreFlightsArrivals={seeMoreFlightsArrivals}
                        seeMoreFlightsDepartures={seeMoreFlightsDepartures}
                        showTime={showTime}
                        timeLabel={timeLabel}
                        handleDirectionChange={handleDirectionChange}
                        handleDateChange={handleDateChange}
                        locale={locale === "da" ? da : enGB}
                        handleDatepicker={handleDatepicker}
                        closeDatepicker={closeDatepicker}
                      />
                    </OutsideClickTrigger>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="clear"></div>
        </div>
      </header>
    </section>
  )
}
